<template><div><h2 id="sidebar" tabindex="-1" class="d-docsite--header-2">Sidebar <a class="header-anchor d-link d-docsite--link d-link" href="#sidebar" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>.d-gl-sidebar</code> to create a simple 2-column layout with a sidebar and main content area.</p>
<h3 id="css-variables" tabindex="-1" class="d-docsite--header-3">CSS Variables <a class="header-anchor d-link d-docsite--link d-link" href="#css-variables" aria-hidden="true">#</a></h3>
<table>
<thead>
<tr>
<th>CSS Var</th>
<th>Output</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>var(--sidebar-width)</code></td>
<td><code v-pre>minmax(20rem, 30rem);</code></td>
</tr>
<tr>
<td><code v-pre>var(--content-width)</code></td>
<td><code v-pre>minmax(32rem, 90ch);</code></td>
</tr>
</tbody>
</table>
<h3 id="example" tabindex="-1" class="d-docsite--header-3">Example <a class="header-anchor d-link d-docsite--link d-link" href="#example" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-stack as=&quot;header&quot; align=&quot;center&quot; justify=&quot;center&quot; class=&quot;d-p-200 d-w100p d-hmn216&quot;&gt;
  &lt;div class=&quot;d-d-grid d-gl-sidebar d-g-200 d-w100p d-hmn216 d-bar-400 d-of-auto d-bgc-moderate&quot; style=&quot;--sidebar-width: minmax(10rem, 20rem);&quot;&gt;
    &lt;dt-stack direction=&quot;row&quot; align=&quot;center&quot; justify=&quot;center&quot; class=&quot;d-ga-sidebar d-p-200 d-bgc-moderate-opaque d-bar-300&quot;&gt;Sidebar&lt;/dt-stack&gt;
    &lt;dt-stack direction=&quot;row&quot; align=&quot;center&quot; justify=&quot;center&quot; class=&quot;d-ga-content d-p-200 d-bgc-moderate-opaque d-bar-300&quot;&gt;Content&lt;/dt-stack&gt;
  &lt;/div&gt;
&lt;/dt-stack&gt;'>
<dt-stack as="header" align="center" justify="center" class="d-p-200 d-w100p d-hmn216">
  <div class="d-d-grid d-gl-sidebar d-g-200 d-w100p d-hmn216 d-bar-400 d-of-auto d-bgc-moderate" style="--sidebar-width: minmax(10rem, 20rem);">
    <dt-stack direction="row" align="center" justify="center" class="d-ga-sidebar d-p-200 d-bgc-moderate-opaque d-bar-300">Sidebar</dt-stack>
    <dt-stack direction="row" align="center" justify="center" class="d-ga-content d-p-200 d-bgc-moderate-opaque d-bar-300">Content</dt-stack>
  </div>
</dt-stack>
</code-example>
<h2 id="header" tabindex="-1" class="d-docsite--header-2">Header <a class="header-anchor d-link d-docsite--link d-link" href="#header" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>.d-gl-header</code> to create a simple 2-row layout with a header area and main content area. Usually this is nested within a <code v-pre>.d-gl-sidebar</code> content area.</p>
<h3 id="css-variables-1" tabindex="-1" class="d-docsite--header-3">CSS Variables <a class="header-anchor d-link d-docsite--link d-link" href="#css-variables-1" aria-hidden="true">#</a></h3>
<table>
<thead>
<tr>
<th>CSS Var</th>
<th>Output</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>var(--header-height)</code></td>
<td><code v-pre>minmax(6.4rem, min-content);</code></td>
</tr>
<tr>
<td><code v-pre>var(--content-height)</code></td>
<td><code v-pre>minmax(64rem, max-content);</code></td>
</tr>
</tbody>
</table>
<h3 id="example-1" tabindex="-1" class="d-docsite--header-3">Example <a class="header-anchor d-link d-docsite--link d-link" href="#example-1" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-stack as=&quot;header&quot; align=&quot;center&quot; justify=&quot;center&quot; class=&quot;d-p-200 d-w100p d-hmn216 d-of-auto&quot;&gt;
  &lt;div class=&quot;d-d-grid d-gl-sidebar d-g-200 d-w100p d-hmn216 d-bar-400 d-bgc-moderate&quot; style=&quot;--sidebar-width: minmax(10rem, 20rem);&quot;&gt;
    &lt;dt-stack direction=&quot;row&quot; align=&quot;center&quot; justify=&quot;center&quot; class=&quot;d-ga-sidebar d-p-200 d-bgc-moderate-opaque d-bar-300&quot;&gt;Sidebar&lt;/dt-stack&gt;
    &lt;div class=&quot;d-ga-content d-d-grid d-gl-header d-g-200 d-p-200 d-bgc-moderate-opaque d-bar-300&quot; style=&quot;--content-height: minmax(24rem, max-content);&quot;&gt;
      &lt;dt-stack direction=&quot;row&quot; align=&quot;center&quot; justify=&quot;center&quot; class=&quot;d-ga-header d-p-200 d-bgc-moderate-opaque d-bar-300&quot;&gt;Header&lt;/dt-stack&gt;
      &lt;dt-stack direction=&quot;row&quot; align=&quot;center&quot; justify=&quot;center&quot; class=&quot;d-ga-content d-p-200 d-bgc-moderate-opaque d-bar-300&quot;&gt;Content&lt;/dt-stack&gt;
    &lt;/div&gt;
  &lt;/div&gt;
&lt;/dt-stack&gt;'>
<dt-stack as="header" align="center" justify="center" class="d-p-200 d-w100p d-hmn216 d-of-auto">
  <div class="d-d-grid d-gl-sidebar d-g-200 d-w100p d-hmn216 d-bar-400 d-bgc-moderate" style="--sidebar-width: minmax(10rem, 20rem);">
    <dt-stack direction="row" align="center" justify="center" class="d-ga-sidebar d-p-200 d-bgc-moderate-opaque d-bar-300">Sidebar</dt-stack>
    <div class="d-ga-content d-d-grid d-gl-header d-g-200 d-p-200 d-bgc-moderate-opaque d-bar-300" style="--content-height: minmax(24rem, max-content);">
      <dt-stack direction="row" align="center" justify="center" class="d-ga-header d-p-200 d-bgc-moderate-opaque d-bar-300">Header</dt-stack>
      <dt-stack direction="row" align="center" justify="center" class="d-ga-content d-p-200 d-bgc-moderate-opaque d-bar-300">Content</dt-stack>
    </div>
  </div>
</dt-stack>
</code-example>
<h2 id="columns" tabindex="-1" class="d-docsite--header-2">Columns <a class="header-anchor d-link d-docsite--link d-link" href="#columns" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>.d-g-cols{n}</code> to create a multi-column layout.</p>
<h3 id="classes" tabindex="-1" class="d-docsite--header-3">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h3>
<utility-class-table>
  <template #content>
    <tbody>
      <tr v-for="i in [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]">
        <th scope="row" class="d-code--sm d-docsite-code">.d-g-cols{{ i }}</th>
        <td class="d-code--sm">grid-template-columns: repeat({{ i }},  minmax(0, 1fr)) !important;</td>
      </tr>
    </tbody>
  </template>
</utility-class-table>
<h3 id="example-2" tabindex="-1" class="d-docsite--header-3">Example <a class="header-anchor d-link d-docsite--link d-link" href="#example-2" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;div class=&quot;d-d-grid d-g-200 d-g-cols4&quot;&gt;
  &lt;div&gt;1&lt;/div&gt;
  &lt;div&gt;2&lt;/div&gt;
  &lt;div&gt;3&lt;/div&gt;
  &lt;div&gt;4&lt;/div&gt;
  &lt;div&gt;5&lt;/div&gt;
  &lt;div&gt;6&lt;/div&gt;
  &lt;div&gt;7&lt;/div&gt;
  &lt;div&gt;8&lt;/div&gt;
&lt;/div&gt;'>
<dt-stack as="header" align="center" justify="center" class="d-w100p d-hmn216">
  <div class="d-d-grid d-g-cols4 d-g-200 d-w100p d-hmn216 d-bar-400 d-bgc-moderate">
    <dt-stack direction="row" align="center" justify="center" class="d-p-200 d-bgc-moderate-opaque d-bar-300">1</dt-stack>
    <dt-stack direction="row" align="center" justify="center" class="d-p-200 d-bgc-moderate-opaque d-bar-300">2</dt-stack>
    <dt-stack direction="row" align="center" justify="center" class="d-p-200 d-bgc-moderate-opaque d-bar-300">3</dt-stack>
    <dt-stack direction="row" align="center" justify="center" class="d-p-200 d-bgc-moderate-opaque d-bar-300">4</dt-stack>
    <dt-stack direction="row" align="center" justify="center" class="d-p-200 d-bgc-moderate-opaque d-bar-300">5</dt-stack>
    <dt-stack direction="row" align="center" justify="center" class="d-p-200 d-bgc-moderate-opaque d-bar-300">6</dt-stack>
    <dt-stack direction="row" align="center" justify="center" class="d-p-200 d-bgc-moderate-opaque d-bar-300">7</dt-stack>
    <dt-stack direction="row" align="center" justify="center" class="d-p-200 d-bgc-moderate-opaque d-bar-300">8</dt-stack>
  </div>
</dt-stack>
</code-example>
<h2 id="nesting-grids" tabindex="-1" class="d-docsite--header-2">Nesting Grids <a class="header-anchor d-link d-docsite--link d-link" href="#nesting-grids" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Unlike some CSS, CSS grid does not cascade beyond the parent and its direct children (<code v-pre>parent-element &gt; *</code>). We can use this to our advantage by being able to nest grids within each other without cascade errors.</p>
<code-example source-code='&lt;div class=&quot;d-d-grid d-g-cols2 d-g-200 d-w100p d-hmn216 d-bar-400 d-bgc-moderate&quot;&gt;
  &lt;dt-stack direction=&quot;row&quot; align=&quot;center&quot; justify=&quot;center&quot; class=&quot;d-p-200 d-bgc-moderate-opaque d-bar-300&quot;&gt;1&lt;/dt-stack&gt;
  &lt;div class=&quot;d-d-grid d-g-cols2 d-g-200 d-p-200 d-bgc-moderate-opaque d-bar-300&quot;&gt;
    &lt;dt-stack direction=&quot;row&quot; align=&quot;center&quot; justify=&quot;center&quot; class=&quot;d-p-200 d-bgc-moderate-opaque d-bar-300&quot;&gt;3&lt;/dt-stack&gt;
    &lt;dt-stack direction=&quot;row&quot; align=&quot;center&quot; justify=&quot;center&quot; class=&quot;d-p-200 d-bgc-moderate-opaque d-bar-300&quot;&gt;4&lt;/dt-stack&gt;
  &lt;/div&gt;
&lt;/div&gt;'>
<div class="d-d-grid d-g-cols2 d-g-200 d-w100p d-hmn216 d-bar-400 d-bgc-moderate">
  <dt-stack direction="row" align="center" justify="center" class="d-p-200 d-bgc-moderate-opaque d-bar-300">1</dt-stack>
  <div class="d-d-grid d-g-cols2 d-g-200 d-p-200 d-bgc-moderate-opaque d-bar-300">
    <dt-stack direction="row" align="center" justify="center" class="d-p-200 d-bgc-moderate-opaque d-bar-300">3</dt-stack>
    <dt-stack direction="row" align="center" justify="center" class="d-p-200 d-bgc-moderate-opaque d-bar-300">4</dt-stack>
  </div>
</div>
</code-example>
</div></template>


